import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@modules/auth/services/auth.service';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';

let isRefreshing = false;
const refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const router = inject(Router);
  const authService = inject(AuthService);

  if (req.headers.get('No-Auth') === 'true') {
    return next(req);
  }

  if (req.url.includes('/connect/token')) {
    return next(req);
  }

  const token = localStorage.getItem('userToken');

  if (authService.isTokenExpiringSoon(5) || (!token && !isRefreshing)) {
    if (!isRefreshing) {
      isRefreshing = true;
      refreshTokenSubject.next(null);

      return authService.refreshToken().pipe(
        switchMap(() => {
          isRefreshing = false;
          const newToken = localStorage.getItem('userToken');
          refreshTokenSubject.next(newToken);
          const newAuthReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${newToken}`,
              'Content-Type': 'application/json'
            }
          });
          return next(newAuthReq);
        }),
        catchError((error) => {
          isRefreshing = false;
          authService.logout();
          router.navigate(['/login'], { queryParams: { returnUrl: router.url } });
          return throwError(() => error);
        })
      );
    } else {
      return refreshTokenSubject.pipe(
        filter(result => result !== null),
        take(1),
        switchMap((newToken) => {
          const newAuthReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${newToken}`,
              'Content-Type': 'application/json'
            }
          });
          return next(newAuthReq);
        })
      );
    }
  }

  const authReq = req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  return next(authReq).pipe(
    catchError((error) => {
      if (error.status === 401) {
        localStorage.removeItem('userToken');
        router.navigate(['/login'], { queryParams: { returnUrl: router.url } });
      }

      return throwError(() => error);
    })
  );
};